import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import OfferDescription from '../OfferDescription'
import OfferLocationHeading from '../OfferLocationHeading'
import useFinestLocationSearchUrl from '../useFinestLocationSearchUrl'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  width: ${rem(80)};
`

const OfferBadgeTagSection = styled.div`
  > * + * {
    margin-inline-start: ${rem(8)};
  }
`

interface Props {
  offer: App.Offer
  locationCaption: string
  bundledOffers: Array<App.BundledOffer>
}

function BundleAndSavePropertyDetails(props: Props) {
  const intl = useIntl()
  const {
    offer,
    locationCaption,
    bundledOffers,
  } = props

  const searchUrl = useFinestLocationSearchUrl(offer.id)
  const offerPropertiesTitle = useMemo(
    () => offer.location || bundledOffers.map(offer => offer.property.name).join(' + '),
    [offer.location, bundledOffers])
  const getPropertyLogoAlt = (propertyName?: string) => propertyName
    ? intl.formatMessage({
        id: 'CWfDGK4FrdaFloQswiXJ',
        defaultMessage: '{propertyName} logo',
      }, { propertyName })
    : intl.formatMessage({
        id: 'G2tm50xP0lyVJ4CSx_pw',
        defaultMessage: 'Vendor logo',
      })

  return (<>
    <Header>
      <Group direction="vertical" gap={8}>
        {/* Bundle & Save has no #getting-there location section, so View on map is omitted. */}
        <OfferLocationHeading
          location={locationCaption}
          searchUrl={searchUrl}
          variant="medium"
        />
        <Heading variant="heading2" as="h1">
          <FormattedMessage
            id="DBgR9ViJ--4ucGkiFs6M"
            defaultMessage="<highlight>Bundle & Save:</highlight> {title}"
            
            values={{
              highlight: (chunks) => <Heading variant="heading2" as="b" colour="highlight-primary-dark">{chunks}</Heading>,
              title: offerPropertiesTitle,
            }}
          />
        </Heading>
        <BodyText variant="large" as="h2">{offer.name}</BodyText>
      </Group>
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" verticalAlign="center" gap={16}>
          <PropertyLogo
            dpr={2}
            enhancedFormats={false}
            id={bundledOffers[0].property.logoImageId}
            alt={getPropertyLogoAlt(bundledOffers[0].property.name)}
          />
          <LinePlusIcon />
          <PropertyLogo
            dpr={2}
            enhancedFormats={false}
            id={bundledOffers[1].property.logoImageId}
            alt={getPropertyLogoAlt(bundledOffers[1].property.name)}
          />
        </Group>
      </CSSBreakpoint>
    </Header>
    {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagSection>
      {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
    </OfferBadgeTagSection>}
    {offer.description && <OfferDescription description={offer.description} />}
  </>)
}

export default BundleAndSavePropertyDetails
